import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { FIDELIDADE_OPCOES, carenciaOpcoesCom, isCarenciaSolicitada } from '../lib/politicaComercial';
import { REGRA_BONIFICACAO, descontoValido, segmentoDe, vsPadrao } from '../lib/bonificacaoParceiro';

interface PlanoParceiroFieldsProps {
    /** Percentuais em escala de exibição (ex: 22, 50). */
    descTarifa: number;
    descBandeira: number;
    onChangeDescTarifa: (v: number) => void;
    onChangeDescBandeira: (v: number) => void;
    /** Consumo somado das UCs (kWh/mês) — define o teto de desconto do segmento. */
    volumeKwh?: number;
    /** Fidelidade (padrão 12 meses). Só renderiza o seletor quando showFidelidade e onChangeFidelidade existem. */
    fidelidade?: string;
    onChangeFidelidade?: (v: string) => void;
    /** Carência — dispara aprovação do gerente/admin quando > 0. */
    carencia?: string;
    onChangeCarencia?: (v: string) => void;
    showFidelidade?: boolean;
    showCarencia?: boolean;
}

const R = REGRA_BONIFICACAO;
const fmtKwh = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });

/**
 * Formulário de plano do PARCEIRO / VENDEDOR PARCEIRO — "jeito de antes" dos multiplicadores.
 * Percentuais manuais de desconto, fidelidade (padrão 12 meses, sem fator de multiplicador) e
 * carência com aprovação. A tela de multiplicadores (planos × público) é exclusiva do vendedor
 * externo/interno; parceiros não têm multiplicador (a remuneração é a bonificação do parceiro).
 *
 * Desconto na tarifa pela regra da bonificação de 26/10/2026 (src/lib/bonificacaoParceiro.ts):
 * 20% é o plano padrão; de 21% até o teto do segmento (C 25%, B 30%, A 35%, pelo consumo) é
 * condição especial, que precisa de aprovação da Vigor (a régua de aprovação de desconto pede
 * ao salvar — ver discountApprovalPolicy) e reduz a bonificação. Só número inteiro.
 */
const PlanoParceiroFields: React.FC<PlanoParceiroFieldsProps> = ({
    descTarifa,
    descBandeira,
    onChangeDescTarifa,
    onChangeDescBandeira,
    volumeKwh,
    fidelidade,
    onChangeFidelidade,
    carencia,
    onChangeCarencia,
    showFidelidade = true,
    showCarencia = true,
}) => {
    const renderFidelidade = showFidelidade && !!onChangeFidelidade;
    const renderCarencia = showCarencia && !!onChangeCarencia;

    // Abaixo de 500 kWh o consumidor não entra na bonificação; o teto de desconto é o do menor segmento.
    const consumo = Number(volumeKwh) || 0;
    const segmento = segmentoDe(Math.max(consumo, R.minimo));
    const teto: number = segmento?.max ?? R.maximo;
    const opcoes = Array.from({ length: teto - R.padrao + 1 }, (_, i) => R.padrao + i);
    const valido = descontoValido(descTarifa) && descTarifa <= teto;
    const atualForaDaRegra = !!descTarifa && !valido;
    const especial = valido && descTarifa > R.autoriza;
    const abaixoDoMinimo = consumo > 0 && consumo < R.minimo;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label htmlFor="plano-parceiro-desc-tarifa" className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Desconto na Tarifa (%)
                    </label>
                    <select
                        id="plano-parceiro-desc-tarifa"
                        required
                        value={descTarifa ? String(descTarifa) : ''}
                        onChange={e => onChangeDescTarifa(Number(e.target.value))}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                    >
                        {!descTarifa && <option value="">Escolha</option>}
                        {atualForaDaRegra && <option value={String(descTarifa)}>{descTarifa}% — atual, fora da regra</option>}
                        {opcoes.map(d => (
                            <option key={d} value={String(d)}>
                                {d}% — {d === R.padrao ? 'plano padrão' : 'condição especial'}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                        Desconto na Bandeira (%)
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            required
                            min="0"
                            max="100"
                            value={descBandeira || ''}
                            onChange={e => onChangeDescBandeira(Number(e.target.value))}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                            placeholder="50"
                        />
                        <span className="absolute right-4 top-2.5 text-gray-400 text-sm">%</span>
                    </div>
                </div>
            </div>

            <div className="-mt-1 space-y-1.5">
                <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                    <Info size={12} className="shrink-0 mt-px" />
                    {segmento && consumo >= R.minimo
                        ? `Segmento ${segmento.n} (${segmento.ate === Infinity ? `acima de ${fmtKwh(segmento.de - 1)}` : `${fmtKwh(segmento.de)} a ${fmtKwh(segmento.ate)}`} kWh): desconto de ${R.padrao}% a ${teto}%, só número inteiro.`
                        : `Desconto de ${R.padrao}% a ${teto}%, só número inteiro. O teto sobe com o consumo (B a partir de 1.500 kWh, A a partir de 5.000 kWh).`}
                </p>
                {especial && (
                    <p className="flex items-start gap-1.5 text-[11px] text-orange-600 dark:text-orange-400">
                        <AlertTriangle size={12} className="shrink-0 mt-px" />
                        <span>
                            Condição especial que precisa de aprovação da Vigor: acima de {R.autoriza}% a solicitação é enviada ao salvar.
                            {' '}Com {descTarifa}% de desconto a bonificação fica em {Math.round(vsPadrao(descTarifa) * 100)}% da do plano padrão.
                        </span>
                    </p>
                )}
                {atualForaDaRegra && (
                    <p className="flex items-start gap-1.5 text-[11px] text-orange-600 dark:text-orange-400">
                        <AlertTriangle size={12} className="shrink-0 mt-px" />
                        O desconto atual ({descTarifa}%) está fora da regra da bonificação ({R.padrao}% a {teto}%): o consumidor não entra na conta do parceiro.
                    </p>
                )}
                {abaixoDoMinimo && (
                    <p className="flex items-start gap-1.5 text-[11px] text-orange-600 dark:text-orange-400">
                        <AlertTriangle size={12} className="shrink-0 mt-px" />
                        Abaixo de {fmtKwh(R.minimo)} kWh: este consumidor não entra na bonificação do parceiro.
                    </p>
                )}
            </div>

            {renderFidelidade && (
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Fidelidade
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {FIDELIDADE_OPCOES.map(o => {
                            const selected = fidelidade === o.valor;
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChangeFidelidade!(o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {renderCarencia && (
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Carência <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(para solicitações)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {carenciaOpcoesCom(carencia).map(o => {
                            const selected = carencia === o.valor || (o.valor === 'sem carência' && !isCarenciaSolicitada(carencia));
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChangeCarencia!(o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {isCarenciaSolicitada(carencia) && (
                        <p className="flex items-center gap-1.5 text-[11px] text-orange-600 dark:text-orange-400 mt-2">
                            <AlertTriangle size={12} className="shrink-0" />
                            Carência exige aprovação do gerente ou admin — a solicitação será enviada ao concluir.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlanoParceiroFields;
